import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, ArrowLeft, ArrowRight, Radio, MapPin, Phone,
  Watch, Home, AlertTriangle
} from "lucide-react";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import FloatingWhatsApp from "../components/FloatingWhatsApp";
import { CmsImageSlideshow as CmsImage } from "../lib/images";
import { CmsText } from "../lib/texts";
import { api } from "../lib/api";
import { useI18n } from "../i18n";
import { waProps, waLabel } from "../lib/whatsapp";

/**
 * Dedicated editorial page for the Sentinel service — satellite location
 * of pre-released individuals. Uses the composited "tablet-with-XEGA-inside"
 * image supplied by the client + a team/hologram operations photo.
 */
export default function Sentinel() {
  const { pick } = useI18n();
  const [content, setContent] = useState(null);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data?.data || null)).catch(() => {});
    api.post("/track/visit", { path: "/servicios/sentinel" }).catch(() => {});
  }, []);

  const CAPABILITIES = [
    { Icon: Home,    t: pick("Llegadas y salidas del domicilio", "Home arrivals & departures", "Chegadas e saídas do domicílio"),
      d: pick("Registra automáticamente cada entrada o salida de la casa del usuario.", "Automatically logs every entry or exit from the user's home.", "Registra automaticamente cada entrada ou saída da casa do usuário.") },
    { Icon: MapPin,  t: pick("Ubicación GPS en todo momento", "GPS location at all times", "Localização GPS em todo momento"),
      d: pick("Ubica al usuario con sistema GPS por satélite, accesible vía internet o desde una agencia.", "Locate the user via satellite GPS, accessible over the internet or from an agency.", "Localiza o usuário via GPS por satélite, acessível pela internet ou por uma agência.") },
    { Icon: Phone,   t: pick("Comunicación vía telefónica", "Phone communication", "Comunicação por telefone"),
      d: pick("El custodio puede llamar al usuario en cualquier momento a través de la telefonía celular.", "The supervisor can call the user any time through cellular telephony.", "O responsável pode ligar para o usuário a qualquer momento via telefonia celular.") },
  ];

  const COMPONENTS = [
    {
      Icon: Watch,
      title: pick("Brazalete", "Bracelet", "Pulseira"),
      bullets: [
        pick("Lo porta el usuario en todo momento — sin posibilidad de quitarlo.", "The user wears it at all times — with no way to remove it.", "O usuário porta o tempo todo — sem possibilidade de retirar."),
        pick("Si el brazalete es dañado o cortado, envía una señal de alarma que es recibida por el custodio.", "If the bracelet is damaged or cut, it sends an alarm signal received by the supervisor.", "Se a pulseira for danificada ou cortada, envia um sinal de alarme recebido pelo responsável."),
      ],
    },
    {
      Icon: Phone,
      title: pick("Teléfono celular con GPS", "GPS-enabled cellphone", "Celular com GPS"),
      bullets: [
        pick("Sistema de localización vía satélite dentro de un sistema de mapas.", "Satellite location system within a mapping system.", "Sistema de localização via satélite dentro de um sistema de mapas."),
        pick("Si el teléfono se separa del brazalete, suena una alarma para avisar al usuario y envía una alerta al custodio.", "If the phone separates from the bracelet, an alarm rings to warn the user and an alert is sent to the supervisor.", "Se o celular se separar da pulseira, um alarme soa para avisar o usuário e envia alerta ao responsável."),
        pick("Botón de emergencia para que el usuario se comunique con el custodio.", "Emergency button for the user to reach the supervisor.", "Botão de emergência para o usuário se comunicar com o responsável."),
      ],
    },
    {
      Icon: Home,
      title: pick("Central de detección domiciliaria", "Home detection unit", "Central de detecção domiciliar"),
      bullets: [
        pick("Se coloca dentro de la casa y detecta cada ingreso o salida, registrando el evento y reportándolo a la central.", "Placed inside the home, detects every entry or exit, logs the event and reports to the control center.", "Instalada dentro da casa, detecta cada entrada ou saída, registra o evento e reporta à central."),
      ],
    },
  ];

  return (
    <div className="min-h-screen text-white" data-testid="sentinel-page">
      <NavBar />

      {/* ═══════ HERO ═══════ */}
      <section className="relative overflow-hidden h-[62vh] min-h-[440px] max-h-[720px] bg-[#06080D]">
        <CmsImage
          slotKey="sentinel.hero"
          defaultSrc="/sentinel/team-hologram.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover object-center opacity-55"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#06080D] via-[#06080D]/70 to-[#06080D]/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#06080D] via-transparent to-transparent" />
        <div className="absolute inset-0 grid-bg opacity-[0.08]" />

        <div className="relative h-full max-w-7xl mx-auto px-5 sm:px-6 md:px-10 flex items-end pb-10 md:pb-14 pt-32 sm:pt-36 md:pt-40">
          <div className="max-w-2xl">
            <Link to="/servicios" className="inline-flex items-center gap-2 text-white/60 hover:text-cyan-300 transition mb-4" data-testid="back-to-services">
              <ArrowLeft className="w-4 h-4" />
              <span className="label-mono">{pick("SERVICIOS / ALL", "SERVICES / ALL", "SERVIÇOS / ALL")}</span>
            </Link>
          </div>
        </div>

        {/* Centered page title over the banner */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="absolute inset-0 flex items-center justify-center pointer-events-none px-4"
          data-testid="sentinel-banner-title"
        >
          <span
            className="font-display chrome-text text-4xl sm:text-6xl md:text-7xl lg:text-[7rem] tracking-[0.08em] leading-none text-center whitespace-nowrap"
            style={{ filter: 'drop-shadow(0 4px 24px rgba(0,0,0,0.85))' }}
          >
            SENTINEL
          </span>
        </motion.h1>
        <div className="absolute bottom-0 inset-x-0 h-10 bg-gradient-to-b from-transparent to-[#06080D] pointer-events-none" />
      </section>

      {/* ═══════ BLOCK #1 — What the system does + tablet with XEGA web ═══════ */}
      <section className="relative py-4 md:py-8 bg-[#06080D] lg:-mt-24 relative z-10">
        <div className="absolute top-1/3 -left-40 w-[560px] h-[560px] rounded-full bg-cyan-500/8 blur-[160px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              transition={{ duration: .7 }}
              className="lg:col-span-6 relative"
            >
              {/* Tablet floats freely on the dark bg thanks to its transparent surround */}
              <div className="relative">
                <div className="absolute inset-0 bg-cyan-500/20 blur-[80px] rounded-full pointer-events-none" />
                <CmsImage
                  slotKey="sentinel.tablet"
                  defaultSrc="/sentinel/tablet-xega.png"
                  alt={pick("Plataforma XEGA en tablet", "XEGA platform on tablet", "Plataforma XEGA em tablet")}
                  className="relative w-full h-auto drop-shadow-[0_25px_45px_rgba(0,0,0,0.6)]"
                  loading="lazy"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: .7, delay: .1 }}
              className="lg:col-span-6"
            >
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl leading-[1.08] chrome-text">
                {pick("Supervisión ", "24/7 supervision, ", "Supervisão ")}
                <span className="text-cyan-300 italic">{pick("24/7", "everywhere", "24/7")}</span>
                {pick(", desde donde estés.", ".", ", de onde estiver.")}
              </h2>
              <p className="mt-5 text-white/85 text-base sm:text-lg leading-relaxed">
                {pick(
                  "Sentinel supervisa a personas bajo régimen pre-liberatorio con protocolo definido, tecnología satelital y respuesta humana desde nuestra sala de control.",
                  "Sentinel supervises individuals under pre-release regimes with a defined protocol, satellite technology and human response from our control room.",
                  "O Sentinel supervisiona pessoas em regime pré-liberatório com protocolo definido, tecnologia satelital e resposta humana da nossa sala de controle."
                )}
              </p>

              {/* 3 capabilities */}
              <ul className="mt-6 space-y-3">
                {CAPABILITIES.map((c, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center bg-cyan-400/15 border border-cyan-400/40 shrink-0">
                      <c.Icon className="w-4 h-4 text-cyan-300" />
                    </span>
                    <div>
                      <div className="font-tech text-sm text-white leading-tight">{c.t}</div>
                      <p className="mt-1 text-[13px] text-white/70 leading-relaxed">{c.d}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════ BLOCK #2 — Components of the system ═══════ */}
      <section className="relative py-4 md:py-8 bg-[#06080D]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <div className="mb-6 max-w-2xl">
            <div className="label-mono mb-3">/ {pick("Equipos", "Equipment", "Equipamentos")}</div>
            <h2 className="font-display text-3xl md:text-4xl chrome-text leading-tight">
              {pick("El sistema se compone de:", "The system is composed of:", "O sistema é composto por:")}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {COMPONENTS.map((c, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: .5, delay: i * 0.06 }}
                className="glass-card chrome-border p-6 group hover:border-cyan-400/50 transition-colors"
                data-testid={`sentinel-component-${i}`}
              >
                <span className="w-10 h-10 rounded-lg flex items-center justify-center bg-cyan-400/15 border border-cyan-400/40 group-hover:bg-cyan-400/25 transition">
                  <c.Icon className="w-5 h-5 text-cyan-300" />
                </span>
                <h3 className="mt-4 font-tech text-base text-white leading-tight">{c.title}</h3>
                <ul className="mt-3 space-y-2">
                  {c.bullets.map((b, k) => (
                    <li key={k} className="flex gap-2 text-[13px] text-white/75 leading-relaxed">
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-cyan-300/70 shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ ALERT BLOCK ═══════ */}
      <section className="relative py-4 md:py-8 bg-[#06080D]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <div className="relative overflow-hidden rounded-3xl chrome-border p-6 sm:p-8 md:p-12">
            <div className="absolute inset-0 bg-gradient-to-br from-red-500/10 via-transparent to-cyan-500/10 pointer-events-none" />
            <div className="relative flex items-start gap-5">
              <span className="w-11 h-11 rounded-xl flex items-center justify-center bg-red-500/15 border border-red-400/40 shrink-0">
                <AlertTriangle className="w-5 h-5" style={{ color: '#ffb0b0' }} />
              </span>
              <div>
                <div className="label-mono mb-2" style={{ color: '#ffb0b0' }}>{pick("SEÑAL DE ALARMA", "ALARM SIGNAL", "SINAL DE ALARME")}</div>
                <h3 className="font-display text-2xl sm:text-3xl leading-tight chrome-text max-w-3xl">
                  {pick(
                    "El sistema activa una señal de alarma cuando el usuario está fuera del inmueble dentro de los horarios no permitidos.",
                    "The system triggers an alarm signal whenever the user is outside the premises during restricted hours.",
                    "O sistema aciona um sinal de alarme sempre que o usuário está fora do imóvel dentro dos horários não permitidos."
                  )}
                </h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ CTA ═══════ */}
      <section className="relative py-4 md:py-8 bg-[#06080D]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <div className="relative overflow-hidden rounded-3xl chrome-border p-8 sm:p-10 md:p-14">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-transparent to-emerald-500/10 pointer-events-none" />
            <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <div className="label-mono mb-3 !text-cyan-300">{pick("¿QUIERES SABER MÁS?", "WANT TO KNOW MORE?", "QUER SABER MAIS?")}</div>
                <h3 className="font-display text-2xl sm:text-3xl md:text-4xl chrome-text leading-tight max-w-xl">
                  <CmsText
                    slotKey="sentinel.cta"
                    es="Hablemos del sistema de localización de Pre-liberado que tu Estado necesita."
                    en="Let's talk about the pre-release location system your State needs."
                    pt="Vamos falar sobre o sistema de localização de pré-liberado que seu Estado precisa."
                    lang={pick("es","en","pt")}
                  />
                </h3>
              </div>
              <a
                {...waProps(content?.contact?.whatsapp ? `https://wa.me/${String(content.contact.whatsapp).replace(/[^0-9]/g,'')}?text=${encodeURIComponent(pick('Hola, me interesa el servicio Sentinel','Hi, I am interested in the Sentinel service','Olá, tenho interesse no serviço Sentinel'))}` : "#contact")}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-cyan-400 to-emerald-400 text-black font-tech uppercase tracking-wider text-sm shadow-[0_0_28px_-4px_rgba(0,240,255,0.7)] hover:shadow-[0_0_36px_-4px_rgba(0,240,255,0.95)] transition self-start md:self-auto"
                data-testid="sentinel-cta-whatsapp"
              >
                {waLabel(pick, "Escribir por WhatsApp", "Message on WhatsApp", "Falar pelo WhatsApp")}
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer content={content} />
      <FloatingWhatsApp phone={content?.contact?.whatsapp} />
    </div>
  );
}
